import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Upload, X, Check, Loader2, RotateCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { isLoggedIn, getRole } from "../lib/auth";
import Header from "../components/Header";
import Footer from "../components/Footer";

const MAX_RETRIES = 3; // total of 4 attempts (initial + 3 retries)
const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000; // 5 min per file
const CONCURRENCY = 2;
const MAX_DIMENSION = 4500; // downscale photos larger than this on the client
const CLIENT_JPEG_QUALITY = 0.92;

/**
 * Browser-side downscale: if the image is larger than MAX_DIMENSION on the
 * longest side, resize and re-encode as JPEG. Faster uploads, less proxy
 * stress, and the watermarked previews are 1400px anyway so we never need
 * 8000px+ originals to reach the server. Photos already smaller are
 * returned untouched.
 */
async function downscaleIfNeeded(file) {
  if (!file.type.startsWith("image/") || file.type === "image/heic" || file.type === "image/heif") {
    return file; // browser can't decode HEIC; let server handle it
  }
  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    const longest = Math.max(width, height);
    if (longest <= MAX_DIMENSION) {
      bitmap.close?.();
      return file;
    }
    const scale = MAX_DIMENSION / longest;
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", CLIENT_JPEG_QUALITY));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.(png|webp|tiff?|heic|heif)$/i, ".jpg"), {
      type: "image/jpeg",
      lastModified: file.lastModified,
    });
  } catch (e) {
    // If anything fails, fall back to the original file
    return file;
  }
}

export default function PhotographerUpload() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [heat, setHeat] = useState("");
  const [classN, setClassN] = useState("");
  const [batchPrice, setBatchPrice] = useState("69");
  const [files, setFiles] = useState([]); // {file, status, photoId, error, progress, attempts}
  const [dragActive, setDragActive] = useState(false);
  const [running, setRunning] = useState(false);
  const inputRef = useRef(null);

  const selectedEvent = events.find((e) => e.id === eventId);

  useEffect(() => {
    if (!isLoggedIn() || getRole() !== "photographer") {
      navigate("/photographer/login");
      return;
    }
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, [navigate]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleFiles = (fileList) => {
    const arr = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    setFiles((prev) => [
      ...prev,
      ...arr.map((file) => ({ file, status: "queued", progress: 0, attempts: 0 })),
    ]);
  };

  const updateFile = (idx, patch) =>
    setFiles((prev) => {
      const next = [...prev];
      if (next[idx]) next[idx] = { ...next[idx], ...patch };
      return next;
    });

  const uploadOne = async (item) => {
    // Downscale once before the first attempt, cache result on the item
    let fileToSend = item.processedFile;
    if (!fileToSend) {
      fileToSend = await downscaleIfNeeded(item.file);
      // Persist to avoid re-processing on retries
      item.processedFile = fileToSend;
    }

    const fd = new FormData();
    fd.append("event_id", eventId);
    if (heat) fd.append("heat", heat);
    if (classN) fd.append("class_name", classN);
    const priceNum = parseFloat(batchPrice);
    if (!isNaN(priceNum) && priceNum >= 10 && priceNum <= 999) {
      fd.append("price_sek", priceNum);
    }
    fd.append("file", fileToSend);

    return api.post("/photos/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: UPLOAD_TIMEOUT_MS,
      onUploadProgress: (ev) => {
        if (ev.total) {
          const pct = Math.round((ev.loaded * 100) / ev.total);
          updateFile(item.idx, { progress: pct });
        }
      },
    });
  };

  const processItem = async (item) => {
    let lastErr = "";
    for (let attempt = 1; attempt <= MAX_RETRIES + 1; attempt++) {
      updateFile(item.idx, { status: "uploading", progress: 0, attempts: attempt });
      try {
        const r = await uploadOne(item);
        updateFile(item.idx, { status: "done", photoId: r.data.id, progress: 100 });
        return true;
      } catch (e) {
        // Build a helpful error message
        if (e?.response?.data?.detail) {
          lastErr = e.response.data.detail;
        } else if (e?.code === "ECONNABORTED") {
          lastErr = "Tidsgräns nådd";
        } else if (e?.message?.toLowerCase().includes("network")) {
          lastErr = "Nätverksfel";
        } else {
          lastErr = e?.message || "Okänt fel";
        }
        if (attempt <= MAX_RETRIES) {
          // Exponential backoff with jitter: 1.5s, 4s, 9s
          const delay = Math.round(1000 * Math.pow(2, attempt - 1) * 1.5 + Math.random() * 500);
          await new Promise((res) => setTimeout(res, delay));
        }
      }
    }
    updateFile(item.idx, { status: "error", error: lastErr, progress: 0 });
    return false;
  };

  const upload = async (onlyFailed = false) => {
    if (!eventId) {
      toast.error("Välj ett event först");
      return;
    }
    if (running) return;
    setRunning(true);

    const targetStatuses = onlyFailed ? ["error"] : ["queued", "error"];
    const targets = files
      .map((f, idx) => ({ ...f, idx }))
      .filter((f) => targetStatuses.includes(f.status));

    if (targets.length === 0) {
      setRunning(false);
      return;
    }

    const queue = [...targets];
    const startedCount = targets.length;

    const workers = Array.from({ length: CONCURRENCY }).map(async () => {
      while (queue.length) {
        const item = queue.shift();
        if (!item) break;
        await processItem(item);
      }
    });
    await Promise.all(workers);

    // Read fresh state for accurate count
    setFiles((prev) => {
      const successAfter = prev.filter((f) => f.status === "done").length;
      const failedAfter = prev.filter((f) => f.status === "error").length;
      if (failedAfter === 0) {
        toast.success(`Klart! ${successAfter} av ${prev.length} foton uppladdade.`);
      } else {
        toast.warning(
          `Klart med varningar: ${successAfter} lyckades, ${failedAfter} misslyckades. Klicka "Försök igen" för att prova igen.`
        );
      }
      return prev;
    });
    setRunning(false);
  };

  const removeQueued = (i) =>
    setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const queuedCount = files.filter((f) => f.status === "queued").length;
  const doneCount = files.filter((f) => f.status === "done").length;
  const errorCount = files.filter((f) => f.status === "error").length;
  const uploadingCount = files.filter((f) => f.status === "uploading").length;

  const totalProgress = files.length
    ? Math.round(
        (files.reduce((acc, f) => {
          if (f.status === "done") return acc + 100;
          if (f.status === "uploading") return acc + (f.progress || 0);
          return acc;
        }, 0) /
          files.length)
      )
    : 0;

  return (
    <div className="App">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/photographer/dashboard"
          className="text-brand hover:underline text-sm mb-4 inline-block"
        >
          ← Tillbaka till dashboard
        </Link>
        <p className="label-overline mb-2">Upload</p>
        <h1 className="font-display font-black uppercase text-white text-4xl sm:text-5xl mb-8">
          Ladda upp foton
        </h1>

        {/* Event selector */}
        {!eventId && (
          <div
            className="bg-brand/10 border-l-4 border-brand p-4 mb-4 flex items-start gap-3"
            data-testid="upload-event-warning"
          >
            <AlertCircle className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-display font-bold uppercase text-white mb-0.5">Välj event först</p>
              <p className="text-white/70">
                Du måste välja vilket event fotona tillhör innan du kan starta uppladdningen. Saknas eventet?{" "}
                <Link to="/photographer/events/new" className="text-brand hover:underline font-bold">
                  Skapa ett nytt event →
                </Link>
              </p>
            </div>
          </div>
        )}

        <div className={`card-surface p-6 mb-6 grid grid-cols-1 md:grid-cols-4 gap-4 ${!eventId ? "ring-2 ring-brand/40" : ""}`}>
          <div>
            <label className="label-overline block mb-1">Event *</label>
            <select
              value={eventId}
              onChange={(e) => {
                setEventId(e.target.value);
                setHeat("");
                setClassN("");
              }}
              className={`input-field ${!eventId ? "border-brand" : ""}`}
              data-testid="upload-event-select"
            >
              <option value="">Välj event</option>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-overline block mb-1">Heat (frivilligt)</label>
            <select
              value={heat}
              onChange={(e) => setHeat(e.target.value)}
              className="input-field"
              disabled={!selectedEvent}
              data-testid="upload-heat-select"
            >
              <option value="">—</option>
              {(selectedEvent?.heats || []).map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-overline block mb-1">Klass (frivilligt)</label>
            <select
              value={classN}
              onChange={(e) => setClassN(e.target.value)}
              className="input-field"
              disabled={!selectedEvent}
              data-testid="upload-class-select"
            >
              <option value="">—</option>
              {(selectedEvent?.classes || []).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-overline block mb-1">Pris per foto (kr)</label>
            <input
              type="number"
              min="10"
              max="999"
              step="1"
              value={batchPrice}
              onChange={(e) => setBatchPrice(e.target.value)}
              className="input-field"
              placeholder="69"
              data-testid="upload-batch-price"
            />
            <p className="text-[10px] text-white/40 mt-0.5">10–999 · gäller alla i denna batch</p>
          </div>
        </div>

        {/* Dropzone */}
        <div
          onDragOver={(e) => {
            if (!eventId) return;
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => {
            if (!eventId) {
              e.preventDefault();
              toast.error("Välj ett event först");
              return;
            }
            onDrop(e);
          }}
          onClick={() => {
            if (!eventId) {
              toast.error("Välj ett event först");
              return;
            }
            inputRef.current?.click();
          }}
          className={`border-2 border-dashed p-10 sm:p-16 text-center transition-colors ${
            !eventId
              ? "border-white/10 bg-white/[0.02] cursor-not-allowed opacity-60"
              : dragActive
              ? "border-brand bg-brand/5 cursor-pointer"
              : "border-white/20 hover:border-brand/50 cursor-pointer"
          }`}
          data-testid="upload-dropzone"
        >
          <Upload className={`w-12 h-12 mx-auto mb-3 ${!eventId ? "text-white/30" : "text-brand"}`} />
          <p className={`font-display uppercase text-2xl ${!eventId ? "text-white/40" : "text-white"}`}>
            {!eventId ? "Välj ett event ovan för att börja" : "Dra & släpp dina foton här"}
          </p>
          <p className="text-white/50 text-sm mt-2">
            {!eventId
              ? "Fotona måste kopplas till ett event så köpare hittar dem"
              : "eller klicka för att välja från din enhet"}
          </p>
          {eventId && (
            <p className="text-white/40 text-xs mt-1">
              JPEG, PNG, WEBP · upp till stora batchar · auto-retry vid nätverksfel
            </p>
          )}
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="upload-input"
          />
        </div>

        {files.length > 0 && (
          <div className="mt-6">
            {/* Status row */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="text-white/70">
                  <span className="text-brand font-bold">{doneCount}</span> klara
                </span>
                {uploadingCount > 0 && (
                  <span className="text-white/70 inline-flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin text-brand" /> {uploadingCount} pågår
                  </span>
                )}
                <span className="text-white/70">{queuedCount} kvar</span>
                {errorCount > 0 && (
                  <span className="text-red-400 inline-flex items-center gap-1" data-testid="error-count">
                    <AlertCircle className="w-3 h-3" /> {errorCount} misslyckade
                  </span>
                )}
                <span className="text-white/40">· {files.length} totalt</span>
              </div>
              <div className="flex gap-2">
                {errorCount > 0 && !running && (
                  <button
                    onClick={() => upload(true)}
                    className="btn-secondary text-sm py-2 px-4 inline-flex items-center gap-2"
                    data-testid="upload-retry-failed"
                  >
                    <RotateCw className="w-3 h-3" /> Försök igen ({errorCount})
                  </button>
                )}
                <button
                  onClick={() => upload(false)}
                  disabled={(queuedCount === 0 && errorCount === 0) || !eventId || running}
                  className="btn-primary disabled:opacity-40 inline-flex items-center gap-2"
                  data-testid="upload-start"
                >
                  {running ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Laddar upp...
                    </>
                  ) : (
                    "Starta uppladdning"
                  )}
                </button>
              </div>
            </div>

            {/* Overall progress bar */}
            {running && (
              <div className="mb-4" data-testid="overall-progress">
                <div className="h-1.5 bg-white/10 overflow-hidden">
                  <div
                    className="h-full bg-brand transition-all duration-300"
                    style={{ width: `${totalProgress}%` }}
                  />
                </div>
                <p className="text-xs text-white/50 mt-1">{totalProgress}% av batchen klar</p>
              </div>
            )}

            <div
              className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2"
              data-testid="upload-queue"
            >
              {files.map((f, i) => (
                <div key={i} className="card-surface p-1 relative" data-testid={`upload-item-${i}`}>
                  <div className="aspect-square overflow-hidden bg-[#1a1a1a] relative">
                    <img
                      src={URL.createObjectURL(f.file)}
                      alt=""
                      className={`w-full h-full object-cover ${f.status === "error" ? "opacity-40" : ""}`}
                    />
                    {f.status === "uploading" && (
                      <div className="absolute inset-x-0 bottom-0 h-1 bg-black/40">
                        <div
                          className="h-full bg-brand transition-all"
                          style={{ width: `${f.progress || 0}%` }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="absolute top-1 right-1">
                    {f.status === "queued" && (
                      <button
                        onClick={() => removeQueued(i)}
                        className="bg-black/70 text-white p-0.5"
                        aria-label="Remove"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                    {f.status === "uploading" && (
                      <Loader2 className="w-5 h-5 text-brand animate-spin bg-black/70 p-0.5" />
                    )}
                    {f.status === "done" && (
                      <Check className="w-5 h-5 text-green-500 bg-black/70 p-0.5" />
                    )}
                    {f.status === "error" && (
                      <AlertCircle className="w-5 h-5 text-red-500 bg-black/70 p-0.5" />
                    )}
                  </div>
                  <p
                    className="text-[10px] text-white/40 truncate p-1"
                    title={f.file.name}
                  >
                    {f.file.name}
                  </p>
                  {f.status === "error" && (
                    <p
                      className="text-[10px] text-red-400 truncate px-1 pb-1"
                      title={f.error}
                      data-testid={`upload-error-${i}`}
                    >
                      {f.error}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
